import { GeneralError } from 'fejl'
import { PrimeMember } from 'iam/MemberStore'
import { GetCurrentUser } from 'iam/SessionStore'
import { collection } from 'infra/collection'
import memoize from 'memoizee'
import { MessagingAPI } from 'messaging/API'
import { VideoCallEvent } from 'messaging/calling/video-call-events'
import { PrimeConversation } from 'messaging/ConversationStore'
import { MessageEventsDTO } from 'messaging/dtos/messages'
import Conversation from 'messaging/models/Conversation'
import { Participant } from 'messaging/models/Participant'
import { concatMap, filter, map, share } from 'rxjs/operators'
import { Id, PrimeProps } from 'types/definitions'
import { filterNonNil } from 'utils/rx-util'
import { VideoCallDto, VideoCallStatusType } from '../dtos/video-calls'
import { VideoCall } from './VideoCall'

export type PrimeVideoCall = (
  conversation: Conversation,
  primeProps: PrimeProps<VideoCallDto, 'id'>
) => VideoCall

/**
 * Call Store.
 */
export default class VideoCallStore {
  /**
   * Call events.
   */
  readonly events$ = this.api.events$.pipe(
    // Ensures events are processed serially.
    concatMap(this.handleServerEvent.bind(this)),
    filterNonNil(),
    share()
  )

  /**
   * Calls collection.
   */
  private readonly calls = collection<VideoCallDto, VideoCall>({
    // A Convo should be fully loaded before attempting to add a call.
    create: (attrs) => {
      const conversation = this.primeConversation({ id: attrs.conversation_id })
      GeneralError.assert(conversation, 'No Conversation returned')
      return new VideoCall(
        conversation,
        this.primeMember,
        this.getTime,
        this.events$
      )
    },
  })

  /**
   * Stream of incoming call events.
   */
  readonly incomingCall$ = this.events$.pipe(
    filter(
      (e) =>
        e.type === 'VideoCallParticipantInvited' &&
        e.participant.member.isCurrentUser
    ),
    map((e) => e.call),
    share()
  )

  constructor(
    private api: MessagingAPI,
    private getTime: () => Date,
    private getCurrentUser: GetCurrentUser,
    private primeMember: PrimeMember,
    private primeConversation: PrimeConversation,
    private fetchConversation: (id: Id) => Promise<Conversation>
  ) {
    this.fetchCall = memoize(this.fetchCall.bind(this), {
      length: 1,
      promise: true,
    })
    this.startCall = this.startCall.bind(this)
    this.joinCall = this.joinCall.bind(this)
  }

  /**
   * Fetches a call by ID.
   * Memoized.
   * @param callId
   */
  async fetchCall(callId: Id) {
    callId = Id.assert(callId)
    const callDto = await this.api.videoCalls.get(callId)
    return this.setCallAsync(callId, callDto)
  }

  /**
   * Starts a call in the conversation and invites the specified conversation
   * participants to it.
   *
   * @param conversation
   * @param participants
   */
  async startCall(
    conversation: Conversation,
    participants: readonly Participant[]
  ) {
    participants = participants.filter(
      (p) => p.member.user.isCurrentUser === false
    )
    const { call: callDto, call_connection_descriptor: connectionDescriptor } =
      await this.api.videoCalls.startOrJoin({
        conversation_id: Id.assert(conversation.id),
        invitee_identities: participants.map((p) => ({
          user_id: Id.assert(p.member.user.id),
          user_public_id: Id.assert(p.member.user.publicId),
        })),
      })

    const call = await this.setCallAsync(callDto.id, callDto)
    return {
      call,
      connectionDescriptor,
    }
  }

  /**
   * Join the call.
   *
   * @param call
   */
  async joinCall(call: VideoCall) {
    const { call_connection_descriptor: connectionDescriptor } =
      await this.api.videoCalls.join(Id.assert(call.id))
    const currentUser = this.getCurrentUser()
    call.ensureParticipant(Id.assert(currentUser.id))
    return {
      call,
      connectionDescriptor,
    }
  }

  /**
   * Hang up the call.
   *
   * @param call
   */
  async hangup(call: VideoCall) {
    const currentUser = this.getCurrentUser()
    call.participants.get(currentUser.id)?.markDisconnected()
    await this.api.videoCalls.hangup(Id.assert(call.id))
    return call
  }

  /**
   * Declines the call.
   *
   * @param call
   */
  async decline(call: VideoCall) {
    const currentUser = this.getCurrentUser()
    call.participants.get(currentUser.id)?.markDeclined()
    await this.api.videoCalls.decline(Id.assert(call.id))
    return call
  }

  /**
   * Primes a call synchronously.
   *
   * @param convo
   * @param props
   */
  primeCall(convo: Conversation, props: PrimeProps<VideoCallDto, 'id'>) {
    return this.calls.set({
      ...props,
      conversation_id: convo.id,
    })
  }

  /**
   * Sets the Call state from a DTO. Ensures
   * that all necessary info is loaded prior,
   *
   * @param callId
   * @param callDto
   * @private
   */
  private async setCallAsync(callId: string, callDto: Partial<VideoCallDto>) {
    const call = this.calls.get(callId)
    if (!call && callDto.conversation_id) {
      // If the call isn't in memory, load the convo.
      // On the other hand, if we already had it then we already
      // loaded the convo.
      await this.fetchConversation(callDto.conversation_id)
    }

    return this.calls.set({
      ...callDto,
      id: callId,
    })
  }

  /**
   * Handles realtime events from the server and maps
   * them to application events.
   *
   * @param event
   * @private
   */
  private async handleServerEvent(
    event: MessageEventsDTO
  ): Promise<VideoCallEvent | null> {
    switch (event.type) {
      case 'CALL_STARTED': {
        return {
          type: 'VideoCallStarted',
          call: await this.setCallAsync(event.payload.id, event.payload),
        }
      }

      case 'CALL_PARTICIPANT_INVITED': {
        const call = await this.fetchCall(event.payload.id)
        const participant = call.ensureParticipant(
          event.payload.participant_user_id
        )
        return {
          call,
          participant,
          type: 'VideoCallParticipantInvited',
        }
      }
      case 'CALL_PARTICIPANT_JOINED': {
        const call = await this.fetchCall(event.payload.id)
        const participant = call.ensureParticipant(
          event.payload.participant_user_id
        )
        return {
          call,
          participant,
          type: 'VideoCallParticipantJoined',
        }
      }

      case 'CALL_PARTICIPANT_DECLINED': {
        const call = await this.fetchCall(event.payload.id)
        const participant = call.ensureParticipant(
          event.payload.participant_user_id
        )
        participant.markDeclined()
        return {
          call,
          participant,
          type: 'VideoCallParticipantDeclined',
        }
      }

      case 'CALL_PARTICIPANT_CONNECTED': {
        const call = await this.fetchCall(event.payload.id)
        const participant = call.ensureParticipant(
          event.payload.participant_user_id
        )
        participant.markConnected()
        return {
          call,
          participant,
          type: 'VideoCallParticipantConnected',
        }
      }

      case 'CALL_PARTICIPANT_DISCONNECTED': {
        const call = await this.fetchCall(event.payload.id)
        const participant = call.ensureParticipant(
          event.payload.participant_user_id
        )
        participant.markDisconnected()
        return {
          call,
          participant,
          type: 'VideoCallParticipantDisconnected',
        }
      }

      case 'CALL_PARTICIPANT_MISSED': {
        const call = await this.fetchCall(event.payload.id)
        const participant = call.ensureParticipant(
          event.payload.participant_user_id
        )
        participant.markMissed()
        return {
          call,
          participant,
          type: 'VideoCallParticipantMissedCall',
        }
      }

      case 'CALL_COMPLETED': {
        const call = await this.setCallAsync(event.payload.id, {
          call_status: VideoCallStatusType.Completed,
          date_completed: event.payload.date_completed,
        })
        return {
          call,
          type: 'VideoCallCompleted',
        }
      }
    }
    return null
  }
}
